import React, { useCallback, useState } from "react";
import { I18n } from "react-redux-i18n";
import isNil from "lodash/isNil";

import { TreeActionType } from "@common/components/Tree";
import { FolderSettingUpdateDto } from "@common/dto/folder-settings";
import { HttpErrorDto } from "@common/dto/http";
import { useFolderController } from "./folders";
import { usePedigreesController } from "./pedigrees";
import { ColumnListDto } from "@common/dto/column/list-dto";
import { NewTableLoadMetadata } from "@common/components/new-table";
import { useIndividualsController } from "./individuals";
import { useInvitesController } from "./invites";
import { FolderListDto } from "@common/dto/folder/list-dto";
import { PedigreeListDto } from "@common/dto/pedigree/list-dto";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { useTableFiltersController } from "@common/hooks/table/filters";
import { AdminSmartListFunctionGetDto } from "@common/dto/admin-function/get";
import { getAdminSmartListFunctions, getUserPermissions } from "@common/actions/permissions";
import { copyPedigree, createPedigreeShortcut, deletePedigreeShortcut, deletePedigrees, importPedigree, movePedigrees, renamePedigree } from "../services/pedigrees";
import { SendInviteParams, sendInvite } from "@common/actions/individuals";
import { ChangeSexParams } from "../services/individuals";
import { FoldersHeaderTextParams } from "@common/components/folders/folders.types";
import { getFilters } from "@common/actions/Filters";
import { useMessage } from "@common/hooks/message";
import { UserPermissions } from "@common/dto/user/permissions";

export const useController = () => {
  const [selectedFolderId, setSelectedFolderId] = useState<FolderListDto['id']>();

  const { showErrorMessage, messageProps, closeMessage, showInfoMessage, showMessage } = useMessage();

  enum TabType {
    individuals = 'individuals',
    invites = 'invites',
  };

  const defaultPedigreeDetailsTabType = TabType.individuals;
  const [selectedPedigreeDetailsTab, setSelectedPedigreeDetailsTab] = React.useState<TabType | undefined>(defaultPedigreeDetailsTabType);
  const [newPedigreeModalOpened, setNewPedigreeModalState] =  React.useState(false);
  const [pedigreeImportModalOpened, setPedigreeImportModalState] =  React.useState(false);
  const [folderPedigreeImportModalOpened, setFolderPedigreeImportModalOpened] =  React.useState(false);
  const [adminFunctions, setAdminFunctions] = React.useState<AdminSmartListFunctionGetDto | undefined>();
  const [userPermissions, setUserPermissions] = React.useState<UserPermissions>({
    admin: false,
    canAddFolder: false,
    canModifyFolder: false,
    canDeleteFolder: false,
  });

  const closeFolderPedigreeImportModal = React.useCallback(() => {
    setFolderPedigreeImportModalOpened(false);
  }, []);

  const openFolderPedigreeImportModal = React.useCallback(() => {
    setFolderPedigreeImportModalOpened(true);
  }, []);

  const handleSubmitFolderPedigreeImport = async (payload: {
    pedigreeName: string;
    file: File;
  }) => {
    await importPedigree({ ...payload, folderId: selectedFolderId! });
    closeFolderPedigreeImportModal();
    showInfoMessage(I18n.t('Pedigree is imported successfully'));
  };

  const closePedigreeImportModal = React.useCallback(() => {
    setPedigreeImportModalState(false);
  }, []);

  const openPedigreeImportModal = React.useCallback(() => {
    setPedigreeImportModalState(true);
  }, []);

  const closeNewPedigreeModal = React.useCallback(() => {
    setNewPedigreeModalState(false);
  }, []);

  const openNewPedigreeModal = React.useCallback(() => {
    setNewPedigreeModalState(true);
  }, []);

  const getColumnId = React.useCallback((column: ColumnListDto) => {
    return `field_id__${column.field.id.toString()}__field_type__${column.field.type.toString()}__context__${column.field.context.toString()}`;
  }, []);

  const {
    pedigreesSearchKeyword,
    pedigrees,
    pedigreesCount,
    pedigreesColumns,
    pedigreesLoadMetadata,
    pedigreesSelectedItems,
    pedigreesLoading,
    pedigreeItemUniqProperty,
    restorePedigreesColumnsToDefault,
    getAndSetPedigreesColumns,
    handleGetPedigrees,
    updatePedigreesColumns,
    setPedigreesSelectedItems,
    getFoldersForPedigreeColumn,
    getModuleFolderFieldsForPedigreeColumn,
    renamePedigreeItem,
    auditPedigreeView
  } = usePedigreesController({
    getColumnId,
  });

  const {
    individuals,
    individualsCount,
    individualsColumns,
    individualsLoadMetadata,
    individualsSelectedItems,
    individualsLoading,
    individualItemUniqProperty,
    handleRestoreIndividualsColumnsToDefault,
    handleIndividualRename,
    getQuestionnairesForNewQuestionnaire,
    handleLaunchNewQuestionnaire,
    getAndSetIndividualsColumns,
    handleGetIndividuals,
    updateIndividualsColumns,
    onRefreshIndividualsOrChangeIndividualsColumns,
    handleIndividualsColumnsOrderChange,
    resetIndividualsList,
    getFoldersForIndividualAndInviteColumn,
    getModuleFolderFieldsForIndividualAndInviteColumn,
    handleIndividualSelectedItemsChange,
    handleChangeIndividualSex
  } = useIndividualsController({
    getColumnId,
    showErrorMessage,
    pedigreesSelectedItems,
    showInfoMessage,
  });

  const {
    invites,
    invitesCount,
    invitesColumns,
    invitesLoadMetadata,
    invitesSelectedItems,
    invitesLoading,
    inviteItemUniqProperty,
    handleUpdateInvitesLockStatus,
    handleInvitesColumnsOrderChange,
    handleRestoreInvitesColumnsToDefault,
    getAndSetInvitesColumns,
    handleGetInvites,
    updateInvitesColumns,
    onRefreshInvitesOrChangeInvitesColumns,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    resetInvitesList,
    handleInviteSelectedItemsChange,
  } = useInvitesController({
    getColumnId,
    showErrorMessage,
    pedigreesSelectedItems,
  });

  const {
    getPedigreesModuleFolders,
    folders,
    loadAndSetFolders,
    loadingFolders,
    folderManagementDialogProps,
    getFolderSecuritySettingsClasses,
    getFolder,
    saveFolder,
    getDefaultFolderId,
    openNewFolderOpen,
    openEditFolderDialog,
    closeFolderManagementDialog,
    deleteFolder,
    renameFolder,
    moveFolder,
    openMoveFolderDialog,
    closeMoveFolderDialog,
    moveFolderDialogProps,
    openPedigreeSpreadsheetForFolder,
    openIndividualSpreadsheetForFolder
  } = useFolderController({
    showErrorMessage,
  });

  const {
    filters,
    initializeFilters,
  } = useTableFiltersController({ getFilters });

  React.useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      getAdminSmartListFunctions()
        .then((res) => {
          setAdminFunctions(res);
        })
        .catch((err) => {
          console.error(err);
          const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get admin functions');
          showErrorMessage(errorMessage);
        });

      getUserPermissions()
        .then((res) => {
          setUserPermissions(res);
        })
        .catch((err) => {
          console.error(err);
          const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get user permissions');
          showErrorMessage(errorMessage);
        });

      initializeFilters().catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get filters');
        showErrorMessage(errorMessage);
      });

      const foldersRes = await loadAndSetFolders();
      const defaultFolderId = getDefaultFolderId(foldersRes);
      const pedigreesColumnsRes = await getAndSetPedigreesColumns();

      if (!isNil(defaultFolderId)) {
        setSelectedFolderId(defaultFolderId);

        if (pedigreesColumnsRes.length) {
          handleGetPedigrees({
            columns: pedigreesColumnsRes,
            folderId: defaultFolderId,
            loadedPedigrees: [],
            pagination: {
              start: 0,
              size: 45,
            },
            sorting: undefined,
          }).catch((err) => {
            console.error(err);
            const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
            showErrorMessage(errorMessage);
          });
        }
      }

      getAndSetIndividualsColumns().catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get individuals columns');
        showErrorMessage(errorMessage);
      });
      getAndSetInvitesColumns().catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get invites columns');
        showErrorMessage(errorMessage);
      });
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get data');
      showErrorMessage(errorMessage);
    }
  };

  const handlePedigreesColumnsOrderChange = async (columns: Array<ColumnListDto>) => {
    const currentSortingColumnExists = columns.some((x) => getColumnId(x) === pedigreesLoadMetadata?.sorting?.id);
    const newPedigreesLoadMetadata: NewTableLoadMetadata = {
      sorting: currentSortingColumnExists 
        ? pedigreesLoadMetadata?.sorting!
        : undefined,
      pagination: {
        start: 0,
        size: 45,
      },
      filtering: undefined,
    };

    await updatePedigreesColumns(columns);
    await getAndSetPedigreesColumns();

    handleGetPedigrees({
      columns,
      folderId: selectedFolderId,
      loadedPedigrees: [],
      searchKeyword: pedigreesSearchKeyword,
      ...newPedigreesLoadMetadata
    }).then(() => {
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    });
  };


  const handleChangeSex = React.useCallback(async (data: ChangeSexParams) => {
    try {
      await handleChangeIndividualSex(data);
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to change individual sex");
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage, handleChangeIndividualSex]);

  const getFoldersActions = () => {
    const actions = [];

    if (userPermissions.canAddFolder) {
      actions.push({ label: I18n.t("New Folder"), onClick: openNewFolderOpen });
    }

    if (userPermissions.admin) {
      actions.push({ label: I18n.t("Edit Folder"), onClick: openEditFolderDialog });
    }

    if (userPermissions.canModifyFolder) {
      actions.push(
          { label: I18n.t("Move Folder"), onClick: openMoveFolderDialog },
          { type: TreeActionType.rename, label: I18n.t("Rename Folder") }
      );
    }

    actions.push(
        { label: I18n.t("Open Pedigree Spreadsheet for Folder"), onClick: openPedigreeSpreadsheetForFolder },
        { label: I18n.t("Open Individual Spreadsheet for Folder"), onClick: openIndividualSpreadsheetForFolder },
        { label: I18n.t("Import pedigree"), onClick: openFolderPedigreeImportModal }
    );

    if (userPermissions.canDeleteFolder) {
      actions.push({ type: TreeActionType.delete, label: I18n.t("Delete Folder") });
    }

    return actions;
  };
  const getFoldersHeaderText = React.useCallback(
    ({ selectedFolderName, totalItemsCount }: FoldersHeaderTextParams) =>
    selectedFolderName
      ? `${totalItemsCount} ${I18n.t("Pedigrees")} ${I18n.t("in")} ${selectedFolderName}`
      : `${totalItemsCount} ${I18n.t("Pedigrees")}`,
    []
  );

  const getFolders = useCallback(async () => {
    try {
      const loadedFolders = await loadAndSetFolders();
      const defaultFolderId = getDefaultFolderId(loadedFolders);
  
      if (isNil(selectedFolderId) && !isNil(defaultFolderId)) {
        setSelectedFolderId(defaultFolderId);
      }
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get folders');
      showErrorMessage(errorMessage);
    }
  }, [loadAndSetFolders, selectedFolderId, getDefaultFolderId, showErrorMessage]);

  const savePedigreeFolder = useCallback(
    async (folderDto: Partial<FolderSettingUpdateDto>) => {
      try {
        await saveFolder(folderDto);
        closeFolderManagementDialog();
        getFolders();
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to save Folder");
        showErrorMessage(errorMessage);
      }
    },
    [closeFolderManagementDialog, saveFolder, getFolders, showErrorMessage]
  );

  const deletePedigreeFolder = useCallback(async (folderId: FolderListDto["id"]) => {
    try {
      await deleteFolder(folderId);
      loadAndSetFolders();
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to delete folder');
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage, loadAndSetFolders]);

  const handlePedigreeSelectedItemsChange = (pedigrees: Array<PedigreeListDto>) => {
    setPedigreesSelectedItems(pedigrees);

    if (pedigrees.length === 0) {
      resetIndividualsList();
      resetInvitesList();
    }

    if (pedigrees.length === 1) {
      const pedigreeId = pedigrees[0]?.id!;

      auditPedigreeView(pedigreeId);

      handleGetIndividuals({
        filtering: individualsLoadMetadata.filtering,
        sorting: individualsLoadMetadata.sorting,
        pagination: {
          size: individualsLoadMetadata.pagination.size,
          start: 0,
        },
        columns: individualsColumns,
        pedigreeId,
        loadedIndividuals: [],
      }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get individuals');
        showErrorMessage(errorMessage);
      });

      handleGetInvites({
        filtering: invitesLoadMetadata.filtering,
        sorting: invitesLoadMetadata.sorting,
        pagination: {
          size: invitesLoadMetadata.pagination.size,
          start: 0,
        },
        columns: invitesColumns,
        pedigreeId,
        loadedInvites: [],
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });
    }
  };

  const handleSelectedFolderChange = React.useCallback(async (folderId: FolderPrimaryListDto['id']) => {
    try {
      setSelectedFolderId(folderId);
      await handleGetPedigrees({
        columns: pedigreesColumns,
        folderId,
        loadedPedigrees: [],
        searchKeyword: '',
        filtering: pedigreesLoadMetadata.filtering,
        sorting: pedigreesLoadMetadata.sorting,
        pagination: {
          size: pedigreesLoadMetadata.pagination.size,
          start: 0,
        }
      });
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    }
  }, [handleGetPedigrees, pedigreesColumns, pedigreesLoadMetadata, pedigreesSearchKeyword]);

  const handlePedigreesSearch = async ({ searchKeyword }: { searchKeyword: string }) => {
    try {
      const folderId = searchKeyword ? undefined : getDefaultFolderId();

      setSelectedFolderId(folderId);
      await handleGetPedigrees({
        searchKeyword,
        columns: pedigreesColumns,
        folderId: folderId,
        loadedPedigrees: [],
        filtering: pedigreesLoadMetadata.filtering,
        sorting: pedigreesLoadMetadata.sorting,
        pagination: {
          size: pedigreesLoadMetadata.pagination.size,
          start: 0,
        }
      });
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    }
  };

  const handleGetPedigreesFromTable = (loadMetadata: NewTableLoadMetadata) => {
    return handleGetPedigrees({
      columns: pedigreesColumns,
      folderId: selectedFolderId,
      loadedPedigrees: pedigrees,
      searchKeyword: pedigreesSearchKeyword,
      ...loadMetadata,
    });
  };

  const handleRestorePedigreesColumnsToDefault = async () => {
    const columns = await restorePedigreesColumnsToDefault();

    handleGetPedigrees({
      columns,
      folderId: selectedFolderId,
      loadedPedigrees: [],
      searchKeyword: pedigreesSearchKeyword,
      pagination: {
        size: 45,
        start: 0,
      },
      sorting: undefined,
      filtering: undefined,
    }).then(() => {
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    });
  };

  const handleCopyPedigree = async (data: { familyId: PedigreeListDto['id']; folderId: FolderPrimaryListDto['id']; pedigreeName: string }) => {
    await copyPedigree(data);

    if (data.folderId === selectedFolderId) {
      handleGetPedigrees({
        columns: pedigreesColumns,
        folderId: selectedFolderId,
        loadedPedigrees: [],
        searchKeyword: pedigreesSearchKeyword,
        pagination: {
          size: 45,
          start: 0,
        },
        sorting: pedigreesLoadMetadata.sorting,
        filtering: pedigreesLoadMetadata.filtering,
      }).then(() => {
        setPedigreesSelectedItems([]);
        resetIndividualsList();
        resetInvitesList();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
        showErrorMessage(errorMessage);
      });
    }
  };

  const handleMovePedigree = async (data: { ids: Array<PedigreeListDto['id']>; folderId: FolderPrimaryListDto['id']; }) => {
    await movePedigrees({
      pedigreeIds: data.ids,
      folderId: data.folderId
    });
    
    await handleGetPedigrees({
      columns: pedigreesColumns,
      folderId: selectedFolderId,
      loadedPedigrees: [],
      searchKeyword: pedigreesSearchKeyword,
      pagination: {
        size: 45,
        start: 0,
      },
      sorting: pedigreesLoadMetadata.sorting,
      filtering: pedigreesLoadMetadata.filtering,
    }).then(() => {
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    });
  };

  const handleRenamePedigree = async (data: { familyId: PedigreeListDto['id']; pedigreeName: string; renameIndividuals: boolean; }) => {
    await renamePedigree({
      ...data,
      folderId: selectedFolderId ?? 0,
    });

    renamePedigreeItem(data.familyId, data.pedigreeName);
  };

  const handleDeletePedigrees = async (pedigrees: Array<PedigreeListDto>) => {
    const itemsToDelete = pedigrees.reduce<{ pedigrees: Array<PedigreeListDto['id']>; shortcuts: Array<string> }>((acc, item) => {
      if (item.shortcut === 'true' && item.shortcutId) {
        acc.shortcuts.push(item.shortcutId);
      }

      if (item.shortcut == 'false' || !item.shortcut) {
        acc.pedigrees.push(item.id);
      }

      return acc;
    }, { pedigrees: [], shortcuts: [] });

    if (itemsToDelete.pedigrees.length) {
      await deletePedigrees(itemsToDelete.pedigrees);
    }

    for (const shortcutId of itemsToDelete.shortcuts) {
      await deletePedigreeShortcut(shortcutId);
    }

    await handleGetPedigrees({
      columns: pedigreesColumns,
      folderId: selectedFolderId,
      loadedPedigrees: [],
      searchKeyword: pedigreesSearchKeyword,
      pagination: {
        size: 45,
        start: 0,
      },
      sorting: pedigreesLoadMetadata.sorting,
      filtering: pedigreesLoadMetadata.filtering,
    }).then(() => {
      setPedigreesSelectedItems([]);
      resetIndividualsList();
      resetInvitesList();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
      showErrorMessage(errorMessage);
    });
  };

  const handleCreatePedigreeShortcut = async (data: { familyId: PedigreeListDto['id']; folderId: FolderPrimaryListDto['id']; shortcutName: string }) => {
    await createPedigreeShortcut(data);

    if (data.folderId === selectedFolderId) {
      handleGetPedigrees({
        columns: pedigreesColumns,
        folderId: selectedFolderId,
        loadedPedigrees: [],
        searchKeyword: pedigreesSearchKeyword,
        pagination: {
          size: 45,
          start: 0,
        },
        sorting: pedigreesLoadMetadata.sorting,
        filtering: pedigreesLoadMetadata.filtering,
      }).then(() => {
        setPedigreesSelectedItems([]);
        resetIndividualsList();
        resetInvitesList();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
        showErrorMessage(errorMessage);
      });
    }
  };

  const handleNewInvitesSend = async (data: Array<SendInviteParams>) => {
    await Promise.all(data.map(sendInvite));
    
    if (pedigreesSelectedItems.length) {
      handleGetInvites({
        columns: invitesColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedInvites: [],
        sorting: invitesLoadMetadata.sorting,
        filtering: invitesLoadMetadata.filtering,
        pagination: {
          start: 0,
          size: 45,
        },
      }).then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
        showErrorMessage(errorMessage);
      });

      handleGetIndividuals({
        filtering: individualsLoadMetadata.filtering,
        sorting: individualsLoadMetadata.sorting,
        pagination: {
          size: individualsLoadMetadata.pagination.size,
          start: 0,
        },
        columns: individualsColumns,
        pedigreeId: pedigreesSelectedItems[0]?.id!,
        loadedIndividuals: [],
      }).then(() => {
        onRefreshIndividualsOrChangeIndividualsColumns();
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get individuals');
        showErrorMessage(errorMessage);
      });
    } else {
      onRefreshInvitesOrChangeInvitesColumns();
      onRefreshIndividualsOrChangeIndividualsColumns();
    }
  };

  return {
    adminFunctions,
    filters,
    showMessage,
    showInfoMessage,
    inviteItemUniqProperty,
    invitesLoading,
    invitesSelectedItems,
    invitesLoadMetadata,
    invitesCount,
    invitesColumns,
    invites,
    handleInviteSelectedItemsChange,
    handleNewInvitesSend,
    handleRestoreInvitesColumnsToDefault,
    updateInvitesColumns,
    handleInvitesColumnsOrderChange,
    handleGetInvites,
    individualItemUniqProperty,
    individuals,
    individualsCount,
    individualsColumns,
    individualsLoadMetadata,
    individualsSelectedItems,
    individualsLoading,
    getQuestionnairesForNewQuestionnaire,
    handleLaunchNewQuestionnaire,
    handleRestoreIndividualsColumnsToDefault,
    handleIndividualRename,
    getModuleFolderFieldsForIndividualAndInviteColumn,
    handleIndividualsColumnsOrderChange,
    updateIndividualsColumns,
    handleGetIndividuals,
    getPedigreesModuleFolders,
    folders,
    loadingFolders,
    selectedFolderId,
    messageProps,
    folderManagementDialogProps,
    pedigreeItemUniqProperty,
    pedigreesCount,
    pedigreesColumns,
    pedigrees,
    pedigreesLoadMetadata,
    pedigreesSelectedItems,
    pedigreesTableOpened: selectedPedigreeDetailsTab !== undefined,
    pedigreesLoading,
    handleGetPedigreesFromTable,
    handlePedigreeSelectedItemsChange,
    selectedPedigreeDetailsTab,
    TabType,
    pedigreesSearchKeyword,
    defaultPedigreeDetailsTabType,
    handleCopyPedigree,
    handleMovePedigree,
    handleRenamePedigree,
    handleDeletePedigrees,
    handleCreatePedigreeShortcut,
    handleRestorePedigreesColumnsToDefault,
    handlePedigreesSearch,
    getModuleFolderFieldsForPedigreeColumn,
    setSelectedPedigreeDetailsTab,
    handlePedigreesColumnsOrderChange,
    updatePedigreesColumns,
    handleSelectedFolderChange,
    getColumnId,
    getFolderSecuritySettingsClasses,
    closeFolderManagementDialog,
    openNewFolderOpen,
    getFoldersActions,
    getFolder,
    showErrorMessage,
    getFolders,
    savePedigreeFolder,
    closeMessage,
    deletePedigreeFolder,
    renameFolder,
    moveFolderDialogProps,
    closeMoveFolderDialog,
    moveFolder,
    newPedigreeModalOpened,
    pedigreeImportModalOpened,
    closePedigreeImportModal,
    openPedigreeImportModal,
    closeNewPedigreeModal,
    openNewPedigreeModal,
    folderPedigreeImportModalOpened,
    closeFolderPedigreeImportModal,
    handleSubmitFolderPedigreeImport,
    getFoldersForIndividualAndInviteColumn,
    getFoldersForPedigreeColumn,
    handleIndividualSelectedItemsChange,
    handleUpdateInvitesLockStatus,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    handleChangeSex: handleChangeSex,
    userPermissions,
    getFoldersHeaderText
  };
};
